import { AlertTriangle, Film } from "lucide-react";
import { useEffect, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

import {
  CONTINUITY_FLAG,
  approveAndPublishDraft,
  continuityApplied,
  continuityRolloutPercent,
  publishContinuitySetting,
  useControlPlaneState,
} from "./controlPlane";

export function ContinuityControlCard() {
  const { state, isLoading, refresh } = useControlPlaneState();
  const payload = state?.status === "ok" ? state.payload : null;
  const currentPercent = payload ? continuityRolloutPercent(payload) : 0;
  const [enabled, setEnabled] = useState(currentPercent > 0);
  const [percent, setPercent] = useState(currentPercent);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    setEnabled(currentPercent > 0);
    setPercent(currentPercent);
  }, [currentPercent]);

  const active = payload?.active_release;
  const applied = payload ? continuityApplied(payload) : false;

  return (
    <Card data-testid="cch-control-card">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Film className="size-4" /> B2C Cinematic Continuity Harness
            </CardTitle>
            <p className="mt-1 font-mono text-xs text-muted-foreground">
              {CONTINUITY_FLAG}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={active ? "outline" : "secondary"}>
              {active ? `release ${active.sequence}` : "no release"}
            </Badge>
            <Badge variant={applied ? "default" : "outline"}>
              {applied ? "orchestrator applied" : "readback pending"}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">
            Reading signed RDS state…
          </p>
        ) : null}
        {state?.status === "unconfigured" ? (
          <p className="text-sm text-muted-foreground">
            The environment-local control-plane endpoint is not provisioned yet.
          </p>
        ) : null}
        {state?.status === "unauthorized" ? (
          <p className="text-sm text-destructive">
            Your verified operator identity has no runtime-config role.
          </p>
        ) : null}
        {state?.status === "error" ? (
          <p className="text-sm text-destructive">{state.message}</p>
        ) : null}
        {payload ? (
          <>
            <div className="flex flex-wrap items-center gap-4">
              <label className="flex items-center gap-2 text-sm font-medium">
                <Switch checked={enabled} onCheckedChange={setEnabled} />
                Master switch
              </label>
              <label className="flex items-center gap-2 text-sm">
                Stable B2C rollout
                <Input
                  aria-label="Stable B2C rollout percent"
                  className="w-24"
                  type="number"
                  min={0}
                  max={100}
                  value={percent}
                  disabled={!enabled}
                  onChange={(event) => setPercent(Number(event.target.value))}
                />
                %
              </label>
              <Button
                disabled={saving}
                onClick={async () => {
                  setSaving(true);
                  setMessage("");
                  try {
                    await publishContinuitySetting(payload, enabled, percent);
                    setMessage(
                      "Signed release published; waiting for orchestrator readback.",
                    );
                    await refresh();
                  } catch (error) {
                    const code =
                      error instanceof Error ? error.message : "publish_failed";
                    setMessage(`Change failed: ${code}`);
                  } finally {
                    setSaving(false);
                  }
                }}
              >
                {saving ? "Publishing…" : "Publish signed release"}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Literal key <code>{payload.environment}</code> means{" "}
              {payload.product_meaning}. Targeting is stable by hashed subject
              key and always hard-gated to explicit B2C.
            </p>
            {payload.environment === "stg" ? (
              <p className="flex items-center gap-2 text-xs text-amber-700">
                <AlertTriangle className="size-4" /> Customer-facing production
                requires explicit approval plus the CCH legal/security release
                gates.
              </p>
            ) : null}
            {payload.pending_drafts?.some(
              (draft) => draft.status === "validated",
            ) ? (
              <div className="space-y-2 rounded-md border p-3">
                <div className="text-sm font-medium">
                  Validated drafts awaiting approval
                </div>
                {payload.pending_drafts
                  .filter((draft) => draft.status === "validated")
                  .map((draft) => (
                    <div
                      key={draft.id}
                      className="flex flex-wrap items-center justify-between gap-2 text-xs"
                    >
                      <div>
                        <span className="font-mono">{draft.id}</span>
                        <span className="ml-2 text-muted-foreground">
                          {draft.reason}
                        </span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={saving}
                        onClick={async () => {
                          setSaving(true);
                          setMessage("");
                          try {
                            await approveAndPublishDraft(draft);
                            setMessage(
                              "Approved release published; waiting for orchestrator readback.",
                            );
                            await refresh();
                          } catch (error) {
                            setMessage(
                              `Approval failed: ${error instanceof Error ? error.message : "unknown_error"}`,
                            );
                          } finally {
                            setSaving(false);
                          }
                        }}
                      >
                        Approve &amp; publish
                      </Button>
                    </div>
                  ))}
              </div>
            ) : null}
            {message ? <p className="text-sm">{message}</p> : null}
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}
